import React from 'react';
import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import { bool, func, shape, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import { compose } from 'redux';

import { AccordionCategory, Button, FieldTextInput, Form } from '../../components';
import config from '../../config';
import {
  ASSESSMENT_ACCREDITATION_CATEGORIES,
  OTHER_OPTION,
} from '../../marketplace-custom-config';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import css from './EditListingAssessmentAccreditationsForm.module.css';

const EditListingAssessmentAccreditationsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        form,
      } = formRenderProps;

      const description = intl.formatMessage({
        id: 'EditListingAssessmentAccreditationsForm.description',
      });
      const textNote = intl.formatMessage({
        id: 'EditListingAssessmentAccreditationsForm.textNote',
      });

      const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
      const errorMessageUpdateListing = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingAssessmentAccreditationsForm.updateFailed" />
        </p>
      ) : null;
      const errorMessageCreateListingDraft = createListingDraftError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingAssessmentAccreditationsForm.createListingDraftError" />
        </p>
      ) : null;
      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingAssessmentAccreditationsForm.showListingFailed" />
        </p>
      ) : null;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const selectedKeys = values?.assessmentAccreditations || [];
      const isOtherSelected = selectedKeys.includes(OTHER_OPTION);
      const submitDisabled =
        disabled ||
        submitInProgress ||
        (isOtherSelected && !values.assessmentAccreditationsOther) ||
        (!isOtherSelected && !!values.assessmentAccreditationsOther);

      const handleToggle = skillKey => {
        const current = values.assessmentAccreditations || [];
        const next = current.includes(skillKey)
          ? current.filter(k => k !== skillKey)
          : [...current, skillKey];
        form.change('assessmentAccreditations', next);
        // Clear other text if "other" is unchecked
        if (skillKey === OTHER_OPTION && current.includes(skillKey)) {
          form.change('assessmentAccreditationsOther', '');
        }
      };

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessageCreateListingDraft}
          {errorMessageUpdateListing}
          {errorMessageShowListing}

          <p className={css.description}>{description}</p>

          <div className={css.accordionContainer}>
            {ASSESSMENT_ACCREDITATION_CATEGORIES.map(category => (
              <AccordionCategory
                key={category.key}
                category={category}
                selectedKeys={selectedKeys}
                onToggleKey={handleToggle}
                bodyFooter={
                  category.key === 'groupOther' && selectedKeys.includes(OTHER_OPTION) ? (
                    <FieldTextInput
                      id="assessmentAccreditationsOther"
                      name="assessmentAccreditationsOther"
                      className={css.otherInput}
                      type="text"
                    />
                  ) : null
                }
                accordionItemClassName={css.accordionItem}
                accordionHeaderClassName={css.accordionHeader}
                accordionHeaderOpenClassName={css.accordionHeaderOpen}
                accordionLabelClassName={css.accordionLabel}
                selectedBadgeClassName={css.selectedBadge}
                accordionChevronClassName={css.accordionChevron}
                accordionBodyClassName={css.accordionBody}
                skillLabelClassName={css.skillLabel}
                skillCheckboxClassName={css.skillCheckbox}
              />
            ))}
          </div>

          <p className={css.textNote}>{textNote}</p>

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingAssessmentAccreditationsFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  filterConfig: config.custom.filters,
};

EditListingAssessmentAccreditationsFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  filterConfig: propTypes.filterConfig,
};

export default compose(injectIntl)(EditListingAssessmentAccreditationsFormComponent);
